import {
  getStoryScrollDistance,
  STORY_PROGRESS,
  STORY_SNAP_POINTS,
} from "./scrollStoryModel";

const present = (value) => (Array.isArray(value) ? value.filter(Boolean) : value);

// Each scene change is an exit followed by an entrance, never two scenes
// moving at once; the gaps between them are the resting states that
// STORY_SNAP_POINTS lands on.
const EXIT = { autoAlpha: 0, scale: 0.9, y: -40, ease: "power2.in", duration: 0.05 };
const ENTER_FROM = { autoAlpha: 0, scale: 0.92, y: 70 };
const ENTER_TO = { autoAlpha: 1, scale: 1, y: 0, ease: "power3.out", duration: 0.08 };
const GAP = 24;

export function createProductStoryTimeline({
  gsap,
  root,
  stage,
  copyItems = [],
  progressItems = [],
  mode = "desktop",
  viewportHeight = window.innerHeight,
}) {
  if (!gsap || !root || !stage) return null;

  const select = (name) => root.querySelector(`[data-story-${name}]`);
  const menu = select("menu");
  const customize = select("customize");
  const qr = select("qr");
  const reservation = select("reservation");
  const dashboard = select("dashboard");
  const layers = [menu, customize, qr, reservation, dashboard];

  if (!layers.every(Boolean)) return null;

  const copies = present(copyItems);
  const progress = present(progressItems);
  const swatches = root.querySelectorAll("[data-custom-swatch]");
  const activeSwatch = root.querySelector('[data-custom-swatch="active"]');
  const qrCard = root.querySelector("[data-qr-card]");
  const qrScan = root.querySelector("[data-qr-scan]");
  const reservationFields = root.querySelectorAll("[data-reservation-field]");
  const selectedTable = root.querySelector(
    '[data-reservation-table="selected"]'
  );
  const confirmation = root.querySelector("[data-reservation-confirm]");
  const dashboardParts = root.querySelectorAll(
    "[data-dashboard-kpi], [data-dashboard-panel]"
  );
  const emojis = root.querySelectorAll("[data-story-emoji]");

  // Menu and editor sit side by side in scene two, clamped to the stage so
  // narrower tablet stages overlap them instead of clipping.
  const pairWidth = () =>
    menu.offsetWidth * 0.94 + customize.offsetWidth + GAP;
  const menuSideX = () =>
    -Math.min(
      (pairWidth() - menu.offsetWidth * 0.94) / 2,
      (stage.offsetWidth - menu.offsetWidth * 0.94) / 2
    );
  const customizeSideX = () =>
    Math.min(
      (pairWidth() - customize.offsetWidth) / 2,
      (stage.offsetWidth - customize.offsetWidth) / 2
    );

  gsap.set([customize, qr, reservation, dashboard], {
    autoAlpha: 0,
    pointerEvents: "none",
  });
  gsap.set(dashboard, { clipPath: "inset(10% 14% round 32px)" });
  gsap.set(copies, { autoAlpha: 0, y: 24 });
  if (copies[0]) gsap.set(copies[0], { autoAlpha: 1, y: 0 });
  gsap.set(progress, { scaleX: 0, transformOrigin: "right center" });
  if (selectedTable) {
    gsap.set(selectedTable, {
      backgroundColor: "#ffffff",
      borderColor: "#e5e7eb",
      color: "#111827",
    });
  }

  const timeline = gsap.timeline({
    defaults: { ease: "none" },
    scrollTrigger: {
      trigger: root,
      start: "top top",
      end: `+=${getStoryScrollDistance(mode, viewportHeight)}`,
      pin: true,
      scrub: 1,
      snap: {
        snapTo: STORY_SNAP_POINTS,
        directional: false,
        inertia: false,
        delay: 0.1,
        duration: { min: 0.25, max: 0.7 },
        ease: "power2.inOut",
      },
      anticipatePin: 1,
      invalidateOnRefresh: true,
    },
  });

  timeline
    .addLabel("menu", 0)
    .addLabel("customize", 0.18)
    .addLabel("qr", 0.38)
    .addLabel("reserve", 0.58)
    .addLabel("dashboard", 0.78);

  // 02 — customize: the menu steps aside and the editor arrives next to it.
  timeline
    .to(
      menu,
      { x: menuSideX, scale: 0.94, duration: 0.08, ease: "power3.inOut" },
      0.14
    )
    .fromTo(
      customize,
      { ...ENTER_FROM, x: customizeSideX },
      { ...ENTER_TO, x: customizeSideX },
      0.16
    )
    .fromTo(
      swatches,
      { scale: 0.6 },
      { scale: 1, stagger: 0.012, duration: 0.05, ease: "back.out(2)" },
      0.2
    );
  if (activeSwatch) {
    timeline.to(
      activeSwatch,
      { scale: 1.25, duration: 0.03, ease: "power2.out" },
      0.25
    );
  }

  // 03 — QR: the pair leaves together, then the table photo rises in.
  timeline
    .to([menu, customize], EXIT, 0.33)
    .fromTo(qr, ENTER_FROM, ENTER_TO, 0.37);
  if (qrCard) {
    timeline.fromTo(
      qrCard,
      { x: 40, autoAlpha: 0 },
      { x: 0, autoAlpha: 1, duration: 0.05, ease: "power3.out" },
      0.41
    );
  }
  if (qrScan) {
    timeline.fromTo(
      qrScan,
      { top: "6%" },
      { top: "92%", duration: 0.05, ease: "power1.inOut" },
      0.43
    );
  }

  // 04 — reservation: fields fill in, a table is picked, then confirmed.
  timeline
    .to(qr, EXIT, 0.53)
    .fromTo(reservation, ENTER_FROM, ENTER_TO, 0.57)
    .fromTo(
      reservationFields,
      { y: 12, autoAlpha: 0 },
      { y: 0, autoAlpha: 1, stagger: 0.012, duration: 0.04, ease: "power2.out" },
      0.61
    );
  if (selectedTable) {
    timeline.to(
      selectedTable,
      {
        backgroundColor: "#f88225",
        borderColor: "#f88225",
        color: "#fff",
        duration: 0.03,
      },
      0.64
    );
  }
  if (confirmation) {
    timeline.fromTo(
      confirmation,
      { y: 12, autoAlpha: 0 },
      { y: 0, autoAlpha: 1, duration: 0.04, ease: "power2.out" },
      0.66
    );
  }

  // 05 — dashboard: opens out from a card into the full management view.
  timeline
    .to(reservation, EXIT, 0.73)
    .fromTo(
      dashboard,
      { autoAlpha: 0, scale: 0.86, y: 50 },
      {
        autoAlpha: 1,
        scale: 1,
        y: 0,
        clipPath: "inset(0% 0% round 24px)",
        duration: 0.1,
        ease: "power3.out",
      },
      0.77
    )
    .fromTo(
      dashboardParts,
      { y: 16, autoAlpha: 0 },
      { y: 0, autoAlpha: 1, stagger: 0.012, duration: 0.06, ease: "power2.out" },
      0.83
    );

  // Food emojis drift at different depths across the whole story.
  emojis.forEach((emoji, index) => {
    const depth = Number(emoji.dataset.depth) || 1;
    timeline.fromTo(
      emoji,
      { y: 60 * depth, rotate: 0 },
      {
        y: -120 * depth,
        rotate: (index % 2 ? -1 : 1) * 14 * depth,
        duration: 1,
      },
      0
    );
  });

  STORY_PROGRESS.forEach(({ start, end }, index) => {
    if (progress[index]) {
      timeline.to(progress[index], { scaleX: 1, duration: end - start }, start);
    }

    if (index === 0) return;
    const previous = copies[index - 1];
    const current = copies[index];
    // The copy swaps inside the visual exit/entrance window, with a short
    // overlap so the text column is never empty.
    if (previous) {
      timeline.to(
        previous,
        { autoAlpha: 0, y: -24, duration: 0.04, ease: "power2.in" },
        start - 0.05
      );
    }
    if (current) {
      timeline.fromTo(
        current,
        { autoAlpha: 0, y: 24 },
        { autoAlpha: 1, y: 0, duration: 0.05, ease: "power2.out" },
        start - 0.015
      );
    }
  });

  return timeline;
}
